import React, { FC, useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import Nav from 'react-bootstrap/Nav';
import Image from 'react-bootstrap/Image';
import styled from 'styled-components';

import avatar from 'assets/avatar-150x150.png';
import profileMenuItems from 'utils/config/profileMenuItems';
import Button from 'components/Button';
import PermissionWrapper from 'components/PermissionWrapper';
import AddToHomeScreenComponent from 'components/add-to-homescreen/AddToHomeScreenComponent';
import { getUserInfo } from 'utils/auth';
import { PermissionKeys } from 'store/types';
import { usePermissionsContext } from 'store/permissions';
import { useTranslation } from 'react-i18next';
import { useTwoFactorContext } from 'store/two-factor';
import SubMenu from './SubMenu';
import ClientWorkMode from './ClientWorkMode';

const ProfileHeader: FC = () => {
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isCursorOnMenu, setIsCursorOnMenu] = useState(false);
  const [referenceElement, setReferenceElement] = useState<HTMLImageElement | null>(null);
  const history = useHistory();
  const location = useLocation();
  const { checkPermission } = usePermissionsContext();
  const { is2FaEnabled } = useTwoFactorContext();

  const { t } = useTranslation();

  const userInfo = getUserInfo();

  useEffect(() => {
    function handleClick() {
      if (!isCursorOnMenu) {
        setIsMenuVisible(false);
      }
    }
    if (isMenuVisible) {
      window.addEventListener('click', handleClick);
      return () => window.removeEventListener('click', handleClick);
    }
  }, [isMenuVisible, isCursorOnMenu]);

  const renderNav = () => {
    return profileMenuItems.map((menuItem, index) => {
      /**
       * If the menu item is 2FA protected (shown only if 2FA flow is active)
       * check if the 2FA flow is enabled and if NOT - don't render item
       */
      if (menuItem.is2FaProtected && !is2FaEnabled) return;

      if (menuItem.addToHomeScreen) {
        return (
          <StyledSubMenuItem
            key={index}
            isActive={location.pathname === menuItem.to}
          >
            <AddToHomeScreenComponent
              icon={menuItem.icon}
              label={menuItem.label}
              setIsMenuVisible={setIsMenuVisible}
            />
          </StyledSubMenuItem>
        );
      }

      return (
        <StyledSubMenuItem
          key={index}
          isActive={location.pathname === menuItem.to}
        >
          <Link
            to={menuItem.to}
            target={menuItem.target}
            className="nav-link"
          >
            {menuItem.icon}
            {t(menuItem.label)}
          </Link>
        </StyledSubMenuItem>
      );
    });
  };

  return (
    <Profile className="profile ml-lg-3 ml-0">
      <Avatar
        src={avatar}
        {...(checkPermission(PermissionKeys.CAN_SWITCH_WORK_MODE)
          ? {
              style: { border: '3px solid' },
              className: `border-${checkPermission(PermissionKeys.CAN_SEE_EMPLOYER_NAV) ? 'secondary' : 'primary'}`,
            }
          : {})}
        roundedCircle
        height={40}
        ref={setReferenceElement}
        onClick={e => {
          e.stopPropagation();
          setIsMenuVisible(!isMenuVisible);
        }}
        onMouseEnter={() => setIsCursorOnMenu(true)}
        onMouseLeave={() => setIsCursorOnMenu(false)}
      />
      <SubMenu
        refElement={referenceElement}
        visible={isMenuVisible}
        onMouseEnter={() => setIsCursorOnMenu(true)}
        onMouseLeave={() => setIsCursorOnMenu(false)}
      >
        <SubMenuHeader>
          <h4>{userInfo.name}</h4>
          <div>{userInfo.email}</div>
        </SubMenuHeader>
        <SubMenuContent>
          <PermissionWrapper permission={PermissionKeys.CAN_SWITCH_WORK_MODE}>
            <ClientWorkMode hideMenu={() => setIsMenuVisible(false)} />
          </PermissionWrapper>
          <SubMenuNav>{renderNav()}</SubMenuNav>
          <ButtonHolder>
            <Button
              className="logout-btn pl-3 pr-3 mt-3"
              btnType="secondary"
              size="sm"
              outlined
              onClick={() => history.push('/logout')}
            >
              {t('menu.logout')}
            </Button>
          </ButtonHolder>
        </SubMenuContent>
      </SubMenu>
    </Profile>
  );
};

export default ProfileHeader;

const Profile = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  @media ${props => props.theme.breakpoints.lg} {
    flex: 1;
  }
`;

const Avatar = styled(Image)`
  cursor: pointer;
`;

const SubMenuHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1.25rem;
  border-bottom: 1px solid ${props => props.theme.colors.light};
`;

const SubMenuContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 1.25rem;
`;

const SubMenuNav = styled(Nav)`
  display: flex;
  flex-direction: column !important;
`;

const StyledSubMenuItem = styled.span<{ isActive: boolean }>`
  a {
    color: ${props => (props.isActive ? props.theme.colors.violet : 'inherit')} !important;

    &:hover {
      color: ${props => props.theme.colors.violet} !important;
    }
  }
`;

const ButtonHolder = styled.div`
  display: flex;
  justify-content: center;
`;
